import React, { createContext, useContext, useState } from 'react';

/**
 * LoadingContext - Contexto global para manejar estados de loading
 *
 * Permite que los estados de operaciones en curso (importaciones, reversiones)
 * persistan durante la navegación entre páginas.
 *
 * USO:
 *
 * const { startOperation, finishOperation, isOperationInProgress } = useLoading();
 *
 * // Iniciar operación
 * startOperation('equipos-import');
 *
 * // Verificar si está en curso
 * const isImporting = isOperationInProgress('equipos-import');
 *
 * // Finalizar operación
 * finishOperation('equipos-import');
 */

const LoadingContext = createContext();

export const useLoading = () => {
  const context = useContext(LoadingContext);
  if (!context) {
    throw new Error('useLoading debe usarse dentro de un LoadingProvider');
  }
  return context;
};

export const LoadingProvider = ({ children }) => {
  // Estado: { 'equipos-import': true, 'mantenimientos-import': true, 'revert-123': true }
  const [operations, setOperations] = useState({});

  /**
   * Inicia una operación (la marca como en progreso)
   * @param {string} operationKey - Identificador único de la operación
   */
  const startOperation = (operationKey) => {
    setOperations(prev => ({ ...prev, [operationKey]: true }));
  };

  /**
   * Finaliza una operación (la marca como completada)
   * @param {string} operationKey - Identificador único de la operación
   */
  const finishOperation = (operationKey) => {
    setOperations(prev => {
      const newOps = { ...prev };
      delete newOps[operationKey];
      return newOps;
    });
  };

  /**
   * Verifica si una operación está en progreso
   * @param {string} operationKey - Identificador único de la operación
   * @returns {boolean}
   */
  const isOperationInProgress = (operationKey) => {
    return !!operations[operationKey];
  };

  /**
   * Obtiene todas las operaciones en progreso
   * @returns {string[]} - Array de keys de operaciones activas
   */
  const getActiveOperations = () => {
    return Object.keys(operations);
  };

  /**
   * Verifica si hay alguna operación en progreso
   * @returns {boolean}
   */
  const hasAnyOperation = () => {
    return Object.keys(operations).length > 0;
  };

  const value = {
    startOperation,
    finishOperation,
    isOperationInProgress,
    getActiveOperations,
    hasAnyOperation,
  };

  return (
    <LoadingContext.Provider value={value}>
      {children}
    </LoadingContext.Provider>
  );
};
